import get from "lodash/get";
import moment from "moment";
import React, { useRef } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { Tooltip as ReactTooltip } from "react-tooltip";
import submit from "submit-form";
import useIsTruncated from "../../hooks/useIsTruncated";
import useSmoothExpand from "../../hooks/useSmoothExpand";
import { RillContext } from "../../rill/RillReact/share/base";
import getCycleCardImagePath from "../../utils/get-cycle-card-image-path";
import getViewInCalendarMonth from "../../utils/get-view-month";
import { pickSavedWorkoutListQuery } from "../../utils/saved-workout-list-query";
import {
	isPushActiveCoach,
	isPushOrPerformClient,
} from "../../utils/user-helpers";
import ImgComponent from "../ImgComponent";
import SvgIcon from "../SvgIcon";
import ToolTip from "../tooltip";
import { clearProgramFetch } from "../../views/coaching-portal/common-components/program-fetch-entry";
import CycleProgram from "./cycle-program";

// Used in program library and program-builder landing page.

// Must match the name's max-height transition in Templates.css so the hidden
// sessions/tags only reappear once the name has finished collapsing.
const NAME_COLLAPSE_MS = 300;

const calculateIfMostPopular = (activeUserToRequest, cycleId, storeType) => {
	const mostPopularName = {
		programs: "mostPopular",
		"single-workout": "mostPopularSingle",
		teams: "mostPopularTeams",
	};

	return (
		activeUserToRequest.pushAccountInfo &&
		activeUserToRequest.pushAccountInfo[mostPopularName[storeType]] &&
		activeUserToRequest.pushAccountInfo[mostPopularName[storeType]] === cycleId
	);
};
class CycleCard extends React.Component {
	constructor(props, context) {
		super(props, context);
		this.state = {
			expanded: false,
			nameExpanded: false,
			hideContent: false,
			storeSubmited: false,
			blockSubmited: false,
			imgError: false,
		};
		this.prefix = this.props.cycle._id.substr(-5);
		this.toggleTags = this.toggleTags.bind(this);
		this.toggleName = this.toggleName.bind(this);
		this.copyToStore = this.copyToStore.bind(this);
		this.imageerror = this.imageerror.bind(this);
	}

	toggleTags() {
		this.setState({ expanded: !this.state.expanded });
	}
	/**
	 * Function to toggle the name of the cycle
	 * @param {React.MouseEvent<HTMLButtonElement>} e
	 */
	toggleName(e) {
		e.stopPropagation();
		e.preventDefault();
		const willExpand = !this.state.nameExpanded;
		clearTimeout(this.revealContentTimer);
		if (willExpand) {
			// Hide sessions/tags right away so the name has room to grow.
			this.setState({ nameExpanded: true, hideContent: true });
		} else {
			// Collapse the name first, then reveal the content once its transition
			// finishes so the card doesn't jump while the name is still tall.
			this.setState({ nameExpanded: false });
			this.revealContentTimer = setTimeout(
				() => this.setState({ hideContent: false }),
				NAME_COLLAPSE_MS,
			);
		}
	}

	componentWillUnmount() {
		clearTimeout(this.revealContentTimer);
	}

	copyToStore(e, cycle) {
		e.stopPropagation();
		e.preventDefault();
		const confirm = window.confirm(
			this.props.t("cycleCard.copyProgramToStoreConfirm"),
		);
		if (!confirm) {
			this.setState({ storeSubmited: false });
			return;
		}

		this.setState({ storeSubmited: true });

		submit(`/actions/cycle/store/${cycle._id}`, {
			method: "GET",
		});
	}

	handleDeleteCycle(e) {
		e.preventDefault();
		submit(`/actions/cycle/delete-cycle/${this.props.cycle._id}`, {
			method: "GET",
		});
	}

	imageerror() {
		this.setState({ imgError: true });
	}
	render() {
		const {
			cycle,
			cardIndex,
			isTemplate,
			isSubscription,
			isSubscriptionPublished,
			t,
		} = this.props;

		const image = getCycleCardImagePath(cycle);

		const style = cycle.type === "premium" ? "alt-premium" : "alt-mine";
		const {
			activeUser,
			activeUserToRequest,
			query = {},
			athlete,
			heading,
		} = this.context.locals;
		const isFe = Boolean(activeUserToRequest.pushAccountInfo);
		const referAsClients = Boolean(activeUser.referAsClients);

		// Determine what "View in calendar" should do
		let viewInCalendarMonth,
			isUnscheduled,
			publishedProgramsOrders = [];
		if (cycle.programs.length) {
			const programProgresses = cycle.programs.map((program) => {
				// take advantage of this iteration to populate published programs orders.
				if (program.status === "published")
					publishedProgramsOrders.push(program.order);
				return program.progress;
			});
			isUnscheduled = programProgresses.every(
				(status) => status === "Unscheduled",
			);
			if (!isUnscheduled) {
				let programToView = cycle.programs.find(
					(program) => program.progress === "In Progress",
				);
				if (!programToView)
					programToView = cycle.programs.find(
						(program) => program.progress === "Upcoming",
					);
				if (!programToView)
					programToView = cycle.programs.find(
						(program) => program.progress === "Completed",
					);
				viewInCalendarMonth = programToView
					? getViewInCalendarMonth(
							programToView.progress,
							programToView.instances,
							this.context.locals.momentTZ,
						)
					: "";
			}
		}

		const isPushed = cycle.status === "pushed";
		const isCoach = cycle.type === "coach";
		const inCoachMode =
			isCoach &&
			String(cycle.createdBy._id) === String(activeUserToRequest._id) &&
			heading !== "Program Builder";
		const fromProgramLibrary = cycle && isSubscriptionPublished;
		// cycle is created by user and auth user is the owner
		const userMode =
			cycle.type === "user" && cycle.createdBy._id === activeUserToRequest._id;
		const typeCard = isTemplate
			? t("cycleCard.template")
			: t("cycleCard.program");
		let organizationQuery = ``;

		if (query.organizationCoach) {
			organizationQuery = `&organizationCoach=${query.organizationCoach}`;
		}

		// Use current URL when available so client-side nav (pushState) is reflected without useEffect
		const urlParams =
			typeof window !== "undefined" && window.location?.search
				? Object.fromEntries(new URLSearchParams(window.location.search))
				: {};
		const templateView = urlParams.templateView ?? query?.templateView;
		const tabView = urlParams.tabView ?? query?.tabView;

		const isTeamsOnAllTemplates =
			(templateView === "teams-programs" || tabView === "teams-programs-tab") &&
			cycle.storeType === "teams";

		const isSingleWorkoutOnAllTemplates =
			(templateView === "single-workouts" ||
				tabView === "single-workouts-tab") &&
			cycle.storeType === "single-workout";

		const parentPath = query.athlete
			? "/athlete-portal/programs"
			: isTeamsOnAllTemplates || isSingleWorkoutOnAllTemplates
				? "/coaching-portal/templates"
				: isSubscription
					? "/coaching-portal/store"
					: isTemplate
						? "/coaching-portal/templates"
						: "/dashboard/program-builder";

		let previewPath = `${parentPath}/program-preview/${cycle._id}?program=1`;
		let addToCalenderPath = `/actions/refresh?modal[type]=add-to-calendar&cycleId=${cycle._id}&wholeCycle=true`;
		if (athlete)
			previewPath =
				`${previewPath}&athlete=${athlete && athlete._id}` + organizationQuery;

		const ListIcon = [
			{
				id: `preview-program`,
				src: "/img/new-icons/eye.svg",
				alt: "preview",
				message: t("cycleCard.previewTypeCard", { typeCard }),
				url: previewPath,
				show: (!isUnscheduled && cycle.programs.length) || isSubscription,
				colorClass: "icon-green",
			},
			{
				id: "fetch-block",
				src: "/img/new-icons/copy-from.svg",
				alt: "fetch",
				message: t("cycleCard.copyBlockFrom"),
				url: `/actions/refresh?modal[type]=fetch&destinationCycleId=${cycle._id}&destinationCycleName=${encodeURIComponent(cycle.name || "")}&destinationCycleStatus=${cycle.status}&fetchType=&fetchSourceOwner=&fetchEntry=`,
				show: true,
				colorClass: "icon-magenta copy-block",
			},
			{
				id: "copy-program",
				src: "/img/new-icons/copy-to.svg",
				alt: "fetch",
				message: t("cycleCard.copyTypeCardTo", { typeCard }),
				url: `/actions/refresh/?modal[type]=send&cycleId=${cycle._id}`,
				show: !isTemplate && !isSubscription,
				colorClass: "icon-blue copy-to",
			},
			{
				id: "duplicate",
				src: "/img/new-icons/duplicate.svg",
				alt: "duplicate",
				message: t("cycleCard.duplicateTypeCard", { typeCard }),
				url: `/actions/cycle/duplicate/${cycle._id}${
					isSubscription ? "?store=true" : ""
				}`,
				show: true,

				showForUser: true,
			},
			{
				id: `program-settings`,
				src: "/img/new-icons/settings.svg",
				alt: "view",
				message: t("cycleCard.typeCardSettings", { typeCard }),
				url: `/actions/refresh?modal[type]=edit-cycle-settings&cycleId=${cycle._id}`,
				show: (!fromProgramLibrary && !isPushed) || isSubscription,

				showForUser: true,
			},
			{
				id: "delete-program",
				src: "/img/new-icons/red-trash.svg",
				alt: "delete",
				message: t("cycleCard.deleteTypeCard", { typeCard }),
				url: `/actions/cycle/delete-cycle/${cycle._id}/?isFromCoachingPortal=${
					inCoachMode || isSubscription
				}`,
				show: isCoach || isSubscription || userMode,
				showForUser: true,
				colorClass: "icon-red",
			},
		];

		const numberOfWeeks = cycle.totalNumberOfWeeks;

		const isMostPopular = calculateIfMostPopular(
			activeUserToRequest,
			cycle._id,
			cycle.storeType || "programs",
		);

		const isProgram = !cycle.storeType || cycle.storeType === "programs";
		const isSingleWorkout = cycle.storeType === "single-workout";
		const isTeams = cycle.storeType === "teams";
		// Templates "Saved Workouts" are coach-type single workouts with no
		// publish/draft lifecycle: always assignable, always editable. Store
		// single-workouts are subscription-type and keep publish/most-popular.
		const isSavedWorkout = isSingleWorkout && isTemplate;
		// A single workout can only be added to an athlete/client once it actually
		// contains exercises.
		const workoutHasContent = (cycle.programs || []).some((program) =>
			(program.sessions || []).some(
				(session) => (session.exercises || []).length > 0,
			),
		);

		return (
			<article
				className={`column ${
					!isProgram && isFe && isSubscriptionPublished ? "pb-0!" : ""
				} ${isTeams && "pb-0!"} `}
				data-col="1/3"
				id={cycle._id}
			>
				<div className={`Cycle-Builder-Preview ${style}`}>
					<div
						className={`image${this.state.imgError ? " error" : ""}`}
						onClick={() => {
							submit(`/actions/refresh/?`, {
								method: "GET",
								body: {
									"modal[type]": "learn-more",
									cycleId: cycle._id,
								},
							});
						}}
					>
						<ImgComponent
							image={image}
							classNames={`program-image ${
								image.startsWith("data:image") ? "no-image" : ""
							}`}
							altText="program thumbnail"
							onErrorFn={this.imageerror}
						/>
					</div>
					{Boolean(cycle.programs.length) &&
						!inCoachMode &&
						!isSubscription && (
							<a
								role="button"
								data-button-type="secondary"
								className="m-4 gap-2"
								href={`/actions/refresh?modal[type]=add-to-calendar&cycleId=${cycle._id}&wholeCycle=true`}
							>
								<SvgIcon src="/img/new-icons/calendar-ico.svg" />
								{t("libraryCycleCard.addToCalendar")}{" "}
							</a>
						)}
					{!!(
						(cycle.programs && cycle.programs.length && isTemplate) ||
						isFe & isSubscriptionPublished
					) && (
						<>
							<header>
								{cycle.programs && cycle.programs.length && isSavedWorkout ? (
									<>
										<button
											data-button-type="secondary"
											className={
												workoutHasContent
													? undefined
													: "opacity-50 cursor-not-allowed"
											}
											data-tooltip-id={`add-workout-${cycle._id}`}
											data-tooltip-content={
												workoutHasContent
													? undefined
													: t("cycleCard.addWorkoutNeedsExercises")
											}
											onClick={(event) => {
												event.stopPropagation();
												if (!workoutHasContent) return;
												submit("/actions/refresh", {
													method: "GET",
													body: {
														"modal[type]": "assign-single-workout-clients",
														cycleId: cycle._id,
													},
												});
											}}
										>
											<SvgIcon
												src="/img/new-icons/calendar.svg"
												alt={
													referAsClients
														? t("cycleCard.addSingleWorkoutToClient")
														: t("cycleCard.addSingleWorkoutToAthlete")
												}
											/>
											{referAsClients
												? t("cycleCard.addSingleWorkoutToClient")
												: t("cycleCard.addSingleWorkoutToAthlete")}
										</button>
										{!workoutHasContent && (
											<ReactTooltip
												id={`add-workout-${cycle._id}`}
												className="React-Tooltip custom-style z-50"
											/>
										)}
									</>
								) : cycle.programs &&
									cycle.programs.length &&
									((isFe &&
										isPushActiveCoach(activeUserToRequest) &&
										isTemplate) ||
										(!isFe && isCoach && isTemplate)) ? (
									<button
										data-button-type="secondary"
										onClick={(event) => {
											event.stopPropagation();
											submit(`/actions/refresh/?`, {
												method: "GET",
												body: {
													"modal[type]": "send",
													cycleId: cycle._id,
												},
											});
										}}
									>
										<SvgIcon
											src="/img/new-icons/user-circle.svg"
											alt="message"
										/>
										{referAsClients
											? t("cycleCard.copyToClient")
											: t("cycleCard.copyToAthlete")}
									</button>
								) : null}
								{isFe &&
									cycle.programs &&
									cycle.programs.length &&
									isTemplate && (
										<button
											data-button-type="secondary"
											onClick={(event) => {
												this.copyToStore(event, cycle);
											}}
										>
											<SvgIcon
												src="/img/new-icons/shopping-bag.svg"
												alt="cStore"
											/>
											{t("cycleCard.copyToStore")}
										</button>
									)}
							</header>
						</>
					)}
					<div className="details">
						<CycleHeader
							cycle={cycle}
							isSubscriptionPublished={isSubscriptionPublished}
							nameExpanded={this.state.nameExpanded}
							onToggleName={this.toggleName}
						/>

						{isTeams && isSubscriptionPublished && (
							<div className="teams-details">
								<div className="team-detail">
									<p>{t("cycleCard.activeMembers")}</p>
									<p>{cycle.members?.length || t("cycleCard.none")}</p>
								</div>
								<div className="team-detail">
									<p>{t("cycleCard.startedOn")}</p>
									<p>
										{moment(cycle?.programs[0]?.startDate, "YYYYMMDD").format(
											"MMM D, YYYY",
										)}
									</p>
								</div>
							</div>
						)}

						{isProgram && !this.state.hideContent && (
							<div className="sessions flex justify-between items-start">
								<ul>
									{cycle.sessionsPerWeek && (
										<li>
											{t("cycleCard.sessionsPerWeek", {
												count: cycle.sessionsPerWeek,
											})}
										</li>
									)}
									<li>
										{numberOfWeeks > 1
											? t("cycleCard.weeksCount", { count: numberOfWeeks })
											: t("cycleCard.weekCount", { count: numberOfWeeks })}
									</li>
								</ul>
								<ToolTip
									id={`${cycle._id}-intro-video`}
									body={t("cycleCard.introVideoTooltip")}
									float={false}
								>
									<span
										className={`chip p-2! ${cycle.video ? "success" : "warning"}`}
									>
										{t("cycleCard.introVideo")}
									</span>
								</ToolTip>
							</div>
						)}
						{isProgram && !this.state.hideContent && (
							<CycleTags
								cycle={cycle}
								state={this.state}
								toggleTags={this.toggleTags}
							/>
						)}

						{!isSubscriptionPublished && isProgram && (
							<ul
								className={`programs ${
									cycle.programs.length < 4 ? "alt-short" : ""
								}`}
								ref="programs"
								data-id={cycle.id}
								data-published-programs={publishedProgramsOrders}
							>
								{cycle.programs.map((program, programIndex) => {
									return (
										<CycleProgram
											key={`${cycle._id}-program-${programIndex}`}
											program={program}
											programIndex={programIndex}
											cycle={cycle}
											cardIndex={cardIndex}
											parentPath={parentPath}
											status={cycle.status}
											type={cycle.type}
											athlete={athlete}
											isTemplate={isTemplate}
											inCoachMode={inCoachMode}
											isCoach={isCoach}
											isSubscription={isSubscription}
											isSubscriptionPublished={isSubscriptionPublished}
											fromProgramLibrary={fromProgramLibrary}
											locals={this.context.locals}
										/>
									);
								})}
							</ul>
						)}

						{isSingleWorkout && (
							<ul
								className={`programs alt-short ${
									isFe && isSubscriptionPublished ? "max-h-[unset]!" : ""
								}`}
								ref="programs"
								data-id={cycle.id}
								data-published-programs={publishedProgramsOrders}
							>
								<li className="tr-Cycle-Program-Item">
									<div className={`tr-content`}>
										<div className="tr-title">
											{t("cycleCard.workoutSettings")}
										</div>
									</div>
									<div className="tr-icon-group">
										<a
											role="button"
											data-button-type="secondary"
											data-icon
											data-icon-only
											className="edit"
											href={
												`/actions/refresh?modal[type]=edit-cycle-settings&cycleId=${cycle._id}&storeType=single-workout` +
												organizationQuery
											}
										>
											<SvgIcon
												className="yellow"
												src="/img/new-icons/settings.svg"
												alt="edit"
											/>
										</a>
									</div>
								</li>

								{!isSavedWorkout && (
									<li className="tr-Cycle-Program-Item h-[42.5px]">
										<div className={`tr-content`}>
											<div className="tr-title">{t("cycleCard.publish")}</div>
										</div>
										<div className="tr-icon-group">
											<input
												type="checkbox"
												role="switch"
												id={`toggle-${cycle._id}`}
												onClick={(event) => {
													const url = `/actions/cycle/${
														event.target.checked ? "publish" : "unpublish"
													}/${cycle._id}?`;
													console.log("url", url);
													event.stopPropagation();
													submit(url, {
														method: "GET",
														body: {
															store: true,
														},
													});
												}}
												checked={isSubscriptionPublished}
											/>
										</div>
									</li>
								)}
								{!isSavedWorkout && isSubscriptionPublished && (
									<li className="tr-Cycle-Program-Item">
										<div className={`tr-content`}>
											<div className="tr-title">
												{referAsClients
													? t("cycleCard.addSingleWorkoutToClient")
													: t("cycleCard.addSingleWorkoutToAthlete")}
											</div>
										</div>
										<div className="tr-icon-group">
											<button
												type="button"
												data-button-type="secondary"
												data-icon
												data-icon-only
												className={`edit${
													workoutHasContent
														? ""
														: " opacity-50 cursor-not-allowed"
												}`}
												title={
													workoutHasContent
														? undefined
														: t("cycleCard.addWorkoutNeedsExercises")
												}
												onClick={(event) => {
													event.stopPropagation();
													if (!workoutHasContent) return;
													submit("/actions/refresh", {
														method: "GET",
														body: {
															"modal[type]": "assign-single-workout-clients",
															cycleId: cycle._id,
														},
													});
												}}
											>
												<SvgIcon
													className="yellow"
													src="/img/new-icons/calendar.svg"
													alt={
														referAsClients
															? t("cycleCard.addSingleWorkoutToClient")
															: t("cycleCard.addSingleWorkoutToAthlete")
													}
												/>
											</button>
										</div>
									</li>
								)}
								{!isSavedWorkout && isFe && isSubscriptionPublished && (
									<li className="tr-Cycle-Program-Item">
										<div className={`tr-content`}>
											<div className="tr-title">
												{t("cycleCard.mostPopular")}
											</div>
										</div>
										<div className="tr-icon-group">
											<button
												data-button-type="secondary"
												data-icon
												data-icon-only
												className="edit"
												onClick={(event) => {
													event.stopPropagation();
													if (isMostPopular) return;
													let body = {
														programId: cycle._id,
														storeType: cycle.storeType || "single-workout",
													};
													if (
														activeUser.organizationId &&
														String(activeUserToRequest._id) !==
															String(activeUser._id)
													) {
														body = {
															...body,
															organizationUserId: activeUserToRequest._id,
														};
													}
													submit("/actions/cycle/store/popular-program", {
														method: "POST",
														body,
													});
												}}
											>
												<SvgIcon
													classNames="popular"
													src={
														isMostPopular
															? "/img/new-icons/golden-star.svg"
															: "/img/new-icons/star.svg"
													}
													noFilterOnLight={isMostPopular ? true : false}
												/>
											</button>
										</div>
									</li>
								)}
								<li className="tr-Cycle-Program-Item">
									<div className={`tr-content`}>
										<div className="tr-title">
											{t("cycleCard.deleteWorkout")}
										</div>
									</div>
									<div className="tr-icon-group">
										<a
											role="button"
											data-button-type="secondary"
											data-icon
											data-icon-only
											className="edit"
											href={`/actions/cycle/delete-cycle/${
												cycle._id
											}/?isFromCoachingPortal=${
												inCoachMode || isSubscription
											}${organizationQuery}`}
										>
											<SvgIcon
												className="yellow"
												src="/img/new-icons/red-trash.svg"
												alt={t("common:edit")}
											/>
										</a>
									</div>
								</li>
							</ul>
						)}

						{isTeams && (
							<ul
								className={`programs alt-short max-h-[unset]!`}
								ref="programs"
								data-id={cycle.id}
								data-published-programs={publishedProgramsOrders}
							>
								<li
									className="tr-Cycle-Program-Item cursor-pointer"
									onClick={() => {
										submit(
											`${parentPath}/teams/${cycle._id}?fromTemplate=true` +
												organizationQuery,
											{ method: "GET" },
										);
									}}
								>
									<div className={`tr-content`}>
										<div className="tr-title">
											{t("cycleCard.teamProgramManager")}
										</div>
									</div>
									<div className="tr-icon-group">
										<a
											role="button"
											data-button-type="secondary"
											data-icon
											data-icon-only
											className="edit"
											href={
												`${parentPath}/teams/${cycle._id}?fromTemplate=true` +
												organizationQuery
											}
										>
											<SvgIcon
												className="yellow"
												src="/img/new-icons/vuesax-linear-document-text.svg"
												alt="edit"
											/>
										</a>
									</div>
								</li>
								<li
									className="tr-Cycle-Program-Item cursor-pointer"
									onClick={() => {
										submit(`/actions/refresh?`, {
											method: "GET",
											body: {
												"modal[type]": "edit-cycle-settings",
												cycleId: cycle._id,
												storeType: "teams",
											},
										});
									}}
								>
									<div className={`tr-content`}>
										<div className="tr-title">
											{t("cycleCard.teamSettings")}
										</div>
									</div>
									<div className="tr-icon-group">
										<a
											role="button"
											data-button-type="secondary"
											data-icon
											data-icon-only
											className="edit"
											href={
												`/actions/refresh?modal[type]=edit-cycle-settings&cycleId=${cycle._id}&storeType=teams` +
												organizationQuery
											}
										>
											<SvgIcon
												className="yellow"
												src="/img/new-icons/settings.svg"
												alt={t("common:edit")}
											/>
										</a>
									</div>
								</li>
								<li className="tr-Cycle-Program-Item h-[42.5px]">
									<div className={`tr-content`}>
										<div className="tr-title">{t("cycleCard.publish")}</div>
									</div>
									<div className="tr-icon-group">
										<input
											type="checkbox"
											role="switch"
											id={`toggle-${cycle._id}`}
											onClick={(event) => {
												event.stopPropagation();

												if (event.target.checked) {
													submit("/actions/refresh", {
														method: "GET",
														body: {
															"modal[type]": "publish-team",
															cycleId: cycle._id,
															cycleName: cycle.name,
														},
													});
												} else {
													submit("/actions/refresh", {
														method: "GET",
														body: {
															"modal[type]": "unpublish-team",
															cycleId: cycle._id,
															cycleName: cycle.name,
														},
													});
												}
											}}
											checked={isSubscriptionPublished}
										/>
									</div>
								</li>
								{isFe && isSubscriptionPublished && (
									<li
										className="tr-Cycle-Program-Item cursor-pointer"
										onClick={(event) => {
											event.stopPropagation();
											if (isMostPopular) return;
											let body = {
												programId: cycle._id,
												storeType: cycle.storeType || "teams",
											};
											if (
												activeUser.organizationId &&
												String(activeUserToRequest._id) !==
													String(activeUser._id)
											) {
												body = {
													...body,
													organizationUserId: activeUserToRequest._id,
												};
											}
											submit("/actions/cycle/store/popular-program", {
												method: "POST",
												body,
											});
										}}
									>
										<div className={`tr-content`}>
											<div className="tr-title">
												{t("cycleCard.mostPopular")}
											</div>
										</div>
										<div className="tr-icon-group">
											<button
												data-button-type="secondary"
												data-icon
												data-icon-only
												className="edit"
												onClick={(event) => {
													event.stopPropagation();
													if (isMostPopular) return;
													let body = {
														programId: cycle._id,
														storeType: cycle.storeType || "teams",
													};
													if (
														activeUser.organizationId &&
														String(activeUserToRequest._id) !==
															String(activeUser._id)
													) {
														body = {
															...body,
															organizationUserId: activeUserToRequest._id,
														};
													}
													submit("/actions/cycle/store/popular-program", {
														method: "POST",
														body,
													});
												}}
											>
												<SvgIcon
													classNames="popular"
													src={
														isMostPopular
															? "/img/new-icons/golden-star.svg"
															: "/img/new-icons/star.svg"
													}
													noFilterOnLight={isMostPopular ? true : false}
												/>
											</button>
										</div>
									</li>
								)}
							</ul>
						)}
					</div>
					{isSubscriptionPublished && isProgram && (
						<footer className="subscriber-footer">
							<div className="experience">
								<p className="p1">{t("cycleCard.experienceLevel")}</p>
								<div className="experience-bar">
									<div className="labels">
										<p className="s3">{t("cycleCard.beginner")}</p>
										<p className="s3">{t("cycleCard.expert")}</p>
									</div>
									<div className="bar">
										<div
											className="status"
											style={
												cycle.difficulty
													? {
															width: {
																Beginner: "33%",
																Intermediate: "50%",
																Expert: "100%",
															}[cycle.difficulty],
														}
													: { width: 0 }
											}
										/>
									</div>
								</div>
							</div>

							<div>
								<div className="flex gap-2 items-center">
									<a
										role="button"
										data-button-type="outline"
										href={previewPath}
									>
										<span>{t("cycleProgram.previewProgram")}</span>
									</a>
									<button
										data-button-type="secondary"
										data-icon
										data-icon-only
										onClick={() => {
											submit(`/actions/refresh/?`, {
												method: "GET",
												body: {
													"modal[type]": "learn-more",
													cycleId: cycle._id,
												},
											});
										}}
									>
										<SvgIcon
											alt="info"
											src="/img/new-icons/w-info-circle.svg"
										/>
									</button>
									{isFe && isSubscriptionPublished && (
										<button
											data-button-type="secondary"
											data-icon
											data-icon-only
											className={`most-popular${
												!isMostPopular ? " small" : ""
											}`}
											onClick={(event) => {
												event.stopPropagation();
												if (isMostPopular) return;
												let body = {
													programId: cycle._id,
													storeType: cycle.storeType || "programs",
												};
												if (
													activeUser.organizationId &&
													String(activeUserToRequest._id) !==
														String(activeUser._id)
												) {
													body = {
														...body,
														organizationUserId: activeUserToRequest._id,
													};
												}
												submit("/actions/cycle/store/popular-program", {
													method: "POST",
													body,
												});
											}}
										>
											<SvgIcon
												alt="popular"
												src={
													isMostPopular
														? "/img/new-icons/golden-star.svg"
														: "/img/new-icons/star.svg"
												}
												noFilterOnLight={isMostPopular ? true : false}
											/>
										</button>
									)}
								</div>

								{isPushOrPerformClient(activeUserToRequest) && (
									<span>
										<a href={addToCalenderPath}>
											{t("libraryCycleCard.addToCalendar")}
											<img
												className="icon-green"
												alt="preview"
												src="/img/svg/calendar-alt.svg"
											/>
										</a>
									</span>
								)}
								<input
									type="checkbox"
									role="switch"
									id={`toggle-${cycle._id}`}
									onClick={(event) => {
										const url = `/actions/cycle/${
											event.target.checked ? "publish" : "unpublish"
										}/${cycle._id}?`;
										event.stopPropagation();
										submit(url, {
											method: "GET",
											body: {
												store: true,
											},
										});
									}}
									checked={isSubscriptionPublished}
								/>
							</div>
						</footer>
					)}

					{!isSubscriptionPublished && isProgram && (
						<footer>
							{String(activeUserToRequest._id) ===
								String(get(cycle, "createdBy._id", cycle.createdBy)) && (
								<button
									className="new-program"
									data-button-type="text"
									data-icon
									onClick={() => {
										if (this.state.blockSubmited) return;
										this.setState({ blockSubmited: true });
										submit(
											`/actions/cycle/${cycle._id}/?proxyAthlete=${query.athlete}${organizationQuery}`,
											{
												method: "POST",
											},
										);
									}}
								>
									<img
										alt={t("common:add")}
										src="/img/new-icons/add-gradient-icon.svg"
									/>
									<span className=" font-semibold">
										{t("cycleCard.addBlock")}
									</span>
								</button>
							)}

							{inCoachMode || isSubscription || userMode ? (
								<div className="tr-icon-group">
									{ListIcon.map(
										({
											src,
											alt,
											id,
											url,
											message,
											show,
											showForUser,
											colorClass,
										}) => {
											return (
												show &&
												!(!showForUser && userMode) && (
													<ToolTip
														id={`${cycle._id}-${id}`}
														body={message}
														key={`${cycle._id}-${id}`}
														float={false}
													>
														<a
															href={url}
															onClick={() => {
																if (id === "fetch-block") clearProgramFetch();
															}}
														>
															<div
																className="bg-(--white-05) p-1 rounded-(--border-radius-md) hover:bg-(--white-20) cursor-pointer"
																id="img-footer-cycle-card-container"
															>
																<SvgIcon
																	className={colorClass ? colorClass : ""}
																	src={src}
																	alt={alt}
																	noFilterOnLight={id === "delete-program"}
																/>
															</div>
														</a>
													</ToolTip>
												)
											);
										},
									)}
									{isSubscription && (
										<input
											type="checkbox"
											role="switch"
											id={`toggle-${cycle._id}`}
											onClick={(event) => {
												const url = `/actions/cycle/${
													event.target.checked ? "publish" : "unpublish"
												}/${cycle._id}?`;
												event.stopPropagation();
												submit(url, {
													method: "GET",
													body: {
														store: true,
													},
												});
											}}
											checked={isSubscriptionPublished}
										/>
									)}
								</div>
							) : (
								<b>
									{cycle.programs.length !== 1
										? t("cycleCard.blocksCount", {
												count: cycle.programs.length,
											})
										: t("cycleCard.blockCount", {
												count: cycle.programs.length,
											})}
								</b>
							)}
						</footer>
					)}

					{isSingleWorkout && (isSavedWorkout || !isSubscriptionPublished) && (
						<footer
							className={
								isSavedWorkout
									? "workout-card-footer-actions"
									: "justify-center!"
							}
						>
							{isSavedWorkout && (
								<button
									type="button"
									className="new-program assign-folder-btn w-1/2"
									data-button-type="outline"
									data-icon
									onClick={(e) => {
										e.stopPropagation();
										submit("/actions/refresh", {
											method: "GET",
											body: {
												"modal[type]": "assign-workout-folder",
												cycleId: cycle._id,
											},
										});
									}}
								>
									<SvgIcon
										src="/img/new-icons/folder-gradient.svg"
										noFilterOnLight={true}
									/>
									<span className="font-semibold">
										{t("cycleCard.assignFolder", "Assign Folder")}
									</span>
								</button>
							)}
							<button
								type="button"
								className="new-program w-1/2"
								data-button-type="outline"
								data-icon
								onClick={() => {
									if (this.state.blockSubmited) return;
									this.setState({ blockSubmited: true });
									// Carry the Saved Workouts list URL (folderId and the
									// other list params) onto the editor so Back can return
									// to this folder. Read the address bar: tab switches
									// are pushState and are not always on locals.query.
									const listSource =
										typeof window !== "undefined" &&
										window.location?.search
											? Object.fromEntries(
													new URLSearchParams(window.location.search),
												)
											: query;
									const listQuery =
										isSavedWorkout && !query.athlete
											? pickSavedWorkoutListQuery(listSource)
											: {};
									submit(
										`${parentPath}/edit-program/${cycle._id}${organizationQuery}`,
										{
											method: "GET",
											body: {
												program: 1,
												athlete: query.athlete,
												...listQuery,
											},
										},
									);
								}}
							>
								<SvgIcon
									src="/img/new-icons/edit-pencil-gradient.svg"
									noFilterOnLight={true}
								/>
								<span className="font-semibold">
									{t("cycleCard.editWorkout")}
								</span>
							</button>
						</footer>
					)}
				</div>
			</article>
		);
	}
}

CycleCard.contextType = RillContext;

export default withTranslation("coaching-portal")(CycleCard);

const CycleHeader = ({
	cycle,
	isSubscriptionPublished,
	nameExpanded,
	onToggleName,
}) => {
	const { t } = useTranslation("coaching-portal");
	const isPullOnly = cycle.storeType === "teams" && cycle.hideFromPush === true;
	const [titleRef, isTruncated] = useIsTruncated();
	useSmoothExpand(titleRef, nameExpanded);
	// Once a name is known to be truncatable, keep the toggle available. The text
	// is unclamped while expanded (so isTruncated reads false then), and program
	// names don't change at runtime, so latching avoids the chevron flickering
	// out for a frame when collapsing.
	const wasExpandable = useRef(false);
	if (isTruncated) {
		wasExpandable.current = true;
	}
	const canToggleName = wasExpandable.current || nameExpanded;
	return (
		<header>
			{cycle.difficulty && !isSubscriptionPublished && (
				<SvgIcon
					src={`/img/new-icons/signal-${cycle.difficulty.toLowerCase()}.svg`}
					alt="signal"
					noFilterOnLight={true}
				/>
			)}
			<h2
				ref={titleRef}
				className={`title ${isSubscriptionPublished ? "title-published" : ""}`}
			>
				{cycle.name}
			</h2>
			{canToggleName && (
				<SvgIcon
					classNames="toggle-name"
					src={`/img/new-icons/${
						nameExpanded ? "arrow-square-up" : "arrow-square-down"
					}.svg`}
					alt={nameExpanded ? "collapse name" : "expand name"}
					onClick={onToggleName}
				/>
			)}
			{isPullOnly && (
				<span className="text-xs px-2! py-0.5! rounded-full bg-(--white-10) text-(--white-70) ml-2">
					{t("store.pullOnlyBadge")}
				</span>
			)}
		</header>
	);
};

const CycleTags = ({ cycle, state, toggleTags }) => {
	return (
		<div className={`tags`}>
			{cycle.goals?.length > 0 && (
				<>
					<ul className={state.expanded ? "opened" : ""}>
						{cycle.goals.map(({ title }, idx) => (
							<li className="chip info" key={`${cycle._id}-tag-${idx}}`}>
								{title}
							</li>
						))}
					</ul>

					{cycle.goals.length >= 3 && (
						<SvgIcon
							src={`/img/new-icons/${
								state.expanded ? "arrow-square-up" : "arrow-square-down"
							}.svg`}
							alt="more"
							onClick={toggleTags}
						/>
					)}
				</>
			)}
		</div>
	);
};
